"use client";
import React from "react";
import { useTranslations } from "next-intl";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";

export const ModifierRuleBadge = ({
  min,
  max,
  isRequirementMet = false,
  isAddtocartClicked = false,
  modifierGroupId,
}: {
  min: number;
  max: number;
  isRequirementMet?: boolean;
  isAddtocartClicked?: boolean;
  modifierGroupId?: string;
}): React.ReactNode | null => {
  const DEFAULT_MIN = 0;
  const DEFAULT_MAX = 2147483647;

  const t = useTranslations("ModifierRuleBadge");

  if (min == DEFAULT_MIN && max == DEFAULT_MAX) {
    return null;
  }

  let badgeText = "";

  if (min > DEFAULT_MIN && max !== DEFAULT_MAX) {
    if (min === max) {
      badgeText = t("required", { min });
    } else if (max > min) {
      badgeText = t("requiredAndMax", { min, max });
    }
  } else if (min > DEFAULT_MIN && max === DEFAULT_MAX) {
    badgeText = t("required", { min });
  } else if (min === DEFAULT_MIN && max !== DEFAULT_MAX && max > 0) {
    badgeText = t("upToMax", { max });
  }

  if (badgeText === "") {
    return null;
  }

  const color = !isRequirementMet
    ? isAddtocartClicked
      ? "red"
      : "grey"
    : "green";

  const status = {
    green: "Met.",
    red: "Not Met.",
    grey: "",
  }[color];

  const statusText = status ? `Status: ${status}` : "";

  return (
    <BadgeComponent
      color={
        !isRequirementMet ? (isAddtocartClicked ? "red" : "grey") : "green"
      }
      text={badgeText}
      statusText={statusText}
      modifierGroupId={modifierGroupId}
    />
  );
};

export const BadgeComponent = ({
  color,
  text,
  statusText,
  modifierGroupId,
}: {
  color: string;
  text: string;
  statusText: string;
  modifierGroupId?: string;
}): React.ReactNode => {
  const badgeId = `badge-${modifierGroupId}`;
  return (
    <div
      id={badgeId}
      className={`bg-[${BadgeColor[color].bg}] rounded-2xl px-3 py-1 text-center ml-2`}
      data-testid={applyDataTestId(pom.itemPage.ruleLabel(modifierGroupId))}
      aria-live="polite"
      aria-atomic="true"
    >
      <span
        style={{ color: BadgeColor[color].text }}
        className={`text-sm font-medium`}
      >
        {text}
      </span>
      {statusText && <span className="sr-only">{statusText}</span>}
    </div>
  );
};

const BadgeColor: { [key: string]: { [key: string]: string } } = {
  green: {
    bg: "#F2FCEE",
    text: "#14570B",
  },
  red: {
    bg: "#FFEDED",
    text: "#C60000",
  },
  grey: {
    bg: "#E5E5E5",
    text: "#393939",
  },
};
